/**
 * Turns a campaign's saved sequence (`list.campaignConfig[uuid].initialNodes`) into a flat
 * list of steps for the read-only "View campaign" summary.
 *
 * Deliberately a summary, not a second campaign builder: the builder renders these nodes
 * on a react-flow canvas with editing attached. Reproducing that here would fork it and
 * drift. This answers "what does this campaign do, in order", and links out for the rest.
 */

/** The step kinds a campaign sequence can hold, mapped to an i18n label. */
export const CAMPAIGN_STEP_KINDS: Record<string, string> = {
	linkedinInviteNode: "linkedinInvite",
	linkedinNode: "linkedinMessage",
	linkedinInmailNode: "linkedinInmail",
	linkedinVisitNode: "linkedinVisit",
	linkedinRemoveInviteNode: "linkedinRemoveInvite",
	emailNode: "email",
};

export interface CampaignStep {
	/** i18n key under `analytics.campaignModal.steps.*`; "other" for an unknown node type. */
	kind: string;
	/** 1-based position among the sending steps. */
	position: number;
	/** Days to wait before this step runs, if the sequence sets one. */
	waitDays: number | null;
	channel: "linkedin" | "email" | "other";
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	!!value && typeof value === "object" && !Array.isArray(value);

const channelOf = (nodeType: string): CampaignStep["channel"] => {
	if (nodeType === "emailNode") {
		return "email";
	}

	return nodeType.startsWith("linkedin") ? "linkedin" : "other";
};

/**
 * The sequence's sending steps in order. Nodes that are not sending steps — the canvas's
 * conditional/branch nodes — are skipped: they shape routing, not what the lead receives.
 */
export function buildCampaignSteps(initialNodes: unknown): CampaignStep[] {
	if (!Array.isArray(initialNodes)) {
		return [];
	}

	const steps: CampaignStep[] = [];

	for (const node of initialNodes) {
		if (!isRecord(node) || typeof node.type !== "string") {
			continue;
		}

		const kind = CAMPAIGN_STEP_KINDS[node.type];

		if (!kind) {
			continue;
		}

		const data = isRecord(node.data) ? node.data : {};
		const days = typeof data.days === "number" ? data.days : Number(data.days);

		steps.push({
			kind,
			position: steps.length + 1,
			waitDays: Number.isFinite(days) && days > 0 ? days : null,
			channel: channelOf(node.type),
		});
	}

	return steps;
}
